import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import AdminNav from "../../components/AdminNav";
import api from "../../utils/api";
import { getSelectedOptionsList } from "../../utils/productOptions";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);

      const response = await api.get(`/orders/admin/all?${params.toString()}`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingStatus(true);
      await api.put(`/orders/admin/${orderId}/status`, { status: newStatus });
      toast.success("Order status updated successfully");
      
      // Update local state
      setOrders(orders.map((ord) => (ord.id === orderId ? { ...ord, status: newStatus } : ord)));
      
      // Update selected order if it's the one being viewed
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
      
      // Refresh orders list
      await fetchOrders();
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openOrderDetails = async (orderId) => {
    try {
      const response = await api.get(`/orders/admin/${orderId}`);
      setSelectedOrder(response.data.order);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      processing: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      shipped: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      delivered: "bg-green-500/20 text-green-400 border-green-500/30",
      cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    return colors[status] || "bg-gray-500/20 text-gray-400 border-gray-500/30";
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500/20 text-yellow-400",
      completed: "bg-green-500/20 text-green-400",
      failed: "bg-red-500/20 text-red-400",
    };
    return colors[status] || "bg-gray-500/20 text-gray-400";
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <AdminNav />
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="heading-metallic text-4xl">Order Management</h1>
            <button onClick={fetchOrders} className="btn-secondary" disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {/* Filters */}
          <div className="card mb-6">
            <div className="flex gap-4 items-center">
              <label className="text-sm font-medium text-gray-300">Filter by Status:</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field">
                <option value="">All Orders</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
              {statusFilter && (
                <button onClick={() => setStatusFilter("")} className="text-gold-500 hover:text-gold-400 text-sm">
                  Clear Filter
                </button>
              )}
            </div>
          </div>

          {/* Orders Table */}
          <div className="card">
            {loading ? (
              <div className="text-center py-12 text-gray-400">Loading orders...</div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400">No orders found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left p-4">Order #</th>
                      <th className="text-left p-4">Customer</th>
                      <th className="text-left p-4">Date</th>
                      <th className="text-left p-4">Items</th>
                      <th className="text-left p-4">Total</th>
                      <th className="text-left p-4">Payment</th>
                      <th className="text-left p-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr
                        key={order.id}
                        onClick={() => openOrderDetails(order.id)}
                        className="border-b border-gray-800 hover:bg-black-lighter cursor-pointer transition-colors"
                      >
                        <td className="p-4 font-mono text-sm text-gold-500">{order.order_number}</td>
                        <td className="p-4">
                          <div>
                            <div className="font-medium">{[order.first_name, order.last_name].filter(Boolean).join(" ") || order.company_name || "Guest"}</div>
                            <div className="text-sm text-gray-400">{order.user_email}</div>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-gray-400">{formatDate(order.created_at)}</td>
                        <td className="p-4 text-center">{Number(order.item_count) || 0}</td>
                        <td className="p-4 font-medium">€{parseFloat(order.total).toFixed(2)}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${getPaymentStatusColor(order.payment_status)}`}>
                            {order.payment_status}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Detail Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setSelectedOrder(null)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-gradient-to-r from-black via-gray-900 to-black rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-700 sticky top-0 bg-gradient-to-r from-black via-gray-900 to-black z-10">
                <div className="flex justify-between items-center">
                  <h2 className="heading-metallic-soft text-2xl">Order #{selectedOrder.order_number}</h2>
                  <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-white">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Order Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="heading-metallic-soft text-lg mb-3">Customer Information</h3>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="text-gray-400">Name:</span> <span className="ml-2">{[selectedOrder.first_name, selectedOrder.last_name].filter(Boolean).join(" ") || selectedOrder.company_name || "N/A"}</span>
                      </p>
                      <p>
                        <span className="text-gray-400">Email:</span> <span className="ml-2">{selectedOrder.user_email || "N/A"}</span>
                      </p>
                      <p>
                        <span className="text-gray-400">Order Date:</span> <span className="ml-2">{formatDate(selectedOrder.created_at)}</span>
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="heading-metallic-soft text-lg mb-3">Order Status</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Current Status:</label>
                        <select value={selectedOrder.status} onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value)} disabled={updatingStatus} className="input-field w-full">
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div>
                        <span className="text-sm text-gray-400">Payment Status:</span>
                        <span className={`ml-2 px-2 py-1 rounded text-xs ${getPaymentStatusColor(selectedOrder.payment_status)}`}>{selectedOrder.payment_status}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-400">Payment Method:</span>
                        <span className="ml-2">{selectedOrder.payment_method || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                {selectedOrder.shipping_address && (
                  <div>
                    <h3 className="heading-metallic-soft text-lg mb-3">Shipping Address</h3>
                    <div className="bg-gray-800 p-4 rounded text-sm">
                      <p>{selectedOrder.shipping_address.addressLine1}</p>
                      {selectedOrder.shipping_address.addressLine2 && <p>{selectedOrder.shipping_address.addressLine2}</p>}
                      <p>
                        {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.postalCode}
                      </p>
                      <p>{selectedOrder.shipping_address.country}</p>
                    </div>
                  </div>
                )}

                {/* Order Items */}
                <div>
                  <h3 className="heading-metallic-soft text-lg mb-3">Order Items</h3>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-gray-800 p-4 rounded">
                        <div className="flex-1">
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-sm text-gray-400">
                            SKU: {item.product_sku} | Qty: {item.quantity}
                          </p>
                          {getSelectedOptionsList(item.selected_options).map((option) => (
                            <p key={`${option.name}-${option.value}`} className="text-sm text-gray-500">
                              {option.name}: {option.value}
                            </p>
                          ))}
                        </div>
                        <div className="text-right">
                          <p className="font-medium">€{parseFloat(item.price).toFixed(2)}</p>
                          <p className="text-sm text-gray-400">Subtotal: €{(item.quantity * item.price).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Total */}
                <div className="border-t border-gray-700 pt-4">
                  <div className="space-y-2 text-right">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Subtotal:</span>
                      <span>€{parseFloat(selectedOrder.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Tax:</span>
                      <span>€{parseFloat(selectedOrder.tax || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Shipping:</span>
                      <span>€{parseFloat(selectedOrder.shipping || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold border-t border-gray-700 pt-2">
                      <span className="text-gold-500">Total:</span>
                      <span className="text-gold-500">€{parseFloat(selectedOrder.total).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Customer Notes */}
                {selectedOrder.customer_notes && (
                  <div>
                    <h3 className="heading-metallic-soft text-lg mb-3">Customer Notes</h3>
                    <div className="bg-gray-800 p-4 rounded text-sm">
                      <p>{selectedOrder.customer_notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
